import React, { ReactNode } from "react";
import { Box, IconButton, styled, Button } from "@mui/material";

import {
  colors,
  fontFamily,
  fontSize,
  fontWeight,
  margin,
  padding,
  borderRadius,
} from "theme/defaultStyle";
import { DialogType } from "utils/custom-hooks/useDialogStyles";

import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from "@mui/icons-material/Close";
import {
  StewartDialog,
  StewartDialogActions,
  StewartDialogContent,
} from "controls/global/dialogs/stewart-dialog/StewartDialog";

const StyledStewartDialog = styled(StewartDialog)({
  fontFamily: fontFamily.primary,
  "& .MuiPaper-root": {
    minWidth: "460px",
    "& .MuiDialogActions-root": {
      justifyContent: "end",
      padding: `${padding.xlarge1} ${padding.xlarge22} ${padding.xlarge22} ${padding.xlarge22}`,
      ".primaryActionButton": {
        marginLeft: margin.large,
      },
    },
  },
});

const CustomDialogContent = styled(StewartDialogContent)({
  maxHeight: "70vh",
  "& .icon-box": {
    backgroundImage: "linear-gradient(rgb(255, 196, 0), rgb(255, 171, 0))",
    color: colors.grey08,
    display: "flex",
    fontFamily: fontFamily.primary,
    fontSize: fontSize.xlarge0,
    fontWeight: fontWeight.normal1,
    justifyContent: "center",
    alignItems: "center",
    float: "left",
    borderRadius: borderRadius.xlarge,
    width: "56px",
  },
  "& .iconButton": {
    padding: padding.xsmall1,
    backgroundColor: colors.grey10,
    position: "absolute",
    right: "45px",
  },
  ".svg_icon": {
    color: colors.grey22,
    width: "24px",
    height: "24px",
  },
  ".message-box": {
    position: "relative",
    textAlign: "left",
    marginLeft: margin.xlarge1,
    fontSize: fontSize.xlarge1,
    fontStyle: "normal",
    fontWeight: fontWeight.bold1,
    lineHeight: "normal",
    marginRight: margin.xlarge50,
  },
  ".belowText": {
    left: "24px",
    fontSize: fontSize.base,
    fontWeight: fontWeight.normal1,
    lineHeight: "normal",
    textAlign: "left",
    margin: `${margin.large} ${margin.xlarge} ${margin.zero} ${margin.xlarge100}`,
  },
  ".contentWrapper": {
    display: "flex",
    margin: `${margin.large} ${margin.large} ${margin.zero}`,
    justifyContent: "flex-start",
    alignItems: "center",
  },
});

type Props = {
  singleActionButton?: boolean;
  singleActionButtonText?: string;
  confirmationMessage: string | ReactNode;
  isOpen: boolean;
  onYes: any;
  disableYes?: boolean;
  onNo?: any;
  disableNo?: boolean;
  buttonNoText?: string;
  noActionButton?: boolean;
  type?: DialogType;
  width?: number;
  textBelowModal?: string;
  iconBoxStyles?: React.CSSProperties;
};

const NoSpecialRisksDialog = ({
  singleActionButton,
  singleActionButtonText,
  confirmationMessage,
  isOpen,
  onYes,
  disableYes = false,
  onNo,
  disableNo = false,
  buttonNoText,
  noActionButton = !singleActionButton,
  type = "WARNING",
  width = 545,
  textBelowModal,
  iconBoxStyles = {
    width: "56px",
    height: "56px",
  },
}: Props) => {
  return (
    <StyledStewartDialog
      open={isOpen}
      width={width}
      sx={{
        "& .MuiPaper-root": {
          width: width ? `${width}px` : "545px !important",
          maxWidth: width ? `${width}px` : "545px !important",
        },
      }}
    >
      <CustomDialogContent type={type}>
        <Box className="contentWrapper">
          <Box className="icon-box" style={iconBoxStyles}>
            <ErrorIcon sx={{ color: 'white' }} fontSize="large" />
          </Box>
          <Box className="message-box">{confirmationMessage}</Box>
          <IconButton className="iconButton" onClick={onNo}>
            <CloseIcon className="svg_icon" />
          </IconButton>
        </Box>
        <Box className="belowText">{textBelowModal}</Box>
      </CustomDialogContent>
      <StewartDialogActions>
        {!singleActionButton && (
          <>
            {noActionButton && (
              <Button variant="outlined" onClick={onNo} disabled={disableNo}>
                {buttonNoText || "No"}
              </Button>
            )}
          </>
        )}
        <Button
          autoFocus
          variant="contained"
          className="primaryActionButton"
          onClick={onYes}
          disabled={disableYes}
        >
          {singleActionButtonText || "Yes"}
        </Button>
      </StewartDialogActions>
    </StyledStewartDialog>
  );
};

export default NoSpecialRisksDialog;
